import { Flex, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handlingFeesApi } from '@texas/api/endpoints/metadata/handlingFeesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { CreateHandlingFee } from './CreateHandlingFee';
import { HandlingFeeModal } from './HandlingFeeModal';

export function HandlingFees() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data, refetch, loading, error } = useApiResource(
    handlingFeesApi.getAll,
  );

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handlingFees = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((item) => !item.archived)
      .filter((item) => item.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  const selected =
    data?.find((handlingFee) => handlingFee.id === selectedId) ?? null;

  return (
    <ConfigurationPage
      title={t('configuration.navigation.handlingFees')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
      action={
        <CreateHandlingFee
          organizationId={currentUser.organizationId}
          onCreated={refetch}
        />
      }
    >
      <HandlingFeeModal
        handlingFee={selected}
        isOpen={selected !== null}
        onClose={() => setSelectedId(null)}
        onSaved={refetch}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('general.value')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && handlingFees.length === 0 && (
              <Tr>
                <Td colSpan={3}>{t('general.noData')}</Td>
              </Tr>
            )}
            {handlingFees.map((handlingFee) => (
              <Tr
                key={handlingFee.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(handlingFee.id)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelectedId(handlingFee.id);
                }}
              >
                <Td>{handlingFee.name}</Td>
                <Td>{handlingFee.value}</Td>
                <Td>{handlingFee.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
